import React, { SyntheticEvent, useEffect, useState } from 'react';
import { Upload, Button } from 'antd';
import {
	LoadingOutlined,
	UploadOutlined,
	DeleteOutlined,
} from '@ant-design/icons';
import {
	UploadChangeParam,
	UploadFile,
	UploadProps,
} from 'antd/lib/upload/interface';
import { RcFile, UploadRequestOption } from 'rc-upload/lib/interface';
import axios from 'axios';
import { useDidUpdate } from 'rooks';
import {
	ControllerRenderProps,
	FieldError,
	useFormContext,
} from 'react-hook-form';
import cx from 'classnames';
import { useTranslation } from 'react-i18next';

import {
	FileModel,
	ImageResolution,
	isFileFormatValid,
	isFileSizeValid,
	isExactResolutionValid,
	isResolutionValid,
} from 'utils/fileUtils';
import api from 'api';

import styles from './ImageFileUpload.module.less';
import OmniNotification from 'components/OmniNotification';
import { isFunction } from 'lodash';

export type FileUploadProps = {
	error?: FieldError;
	uploadLinkFunction: Function;
	field?: ControllerRenderProps;
	defaultImageUrl?: string;
	onLoadingChange?: (loading: boolean) => void;
	buttonText?: string;
	onImageChange?: (image: FileModel | null) => void;
	acceptedResolution?: ImageResolution;
	exactResolution?: ImageResolution;
	projectName?: string;
	customSuccessCallback?: () => void;
} & UploadProps;

export function getResolution(img: RcFile): Promise<ImageResolution | null> {
	return new Promise((resolve, reject) => {
		getBase64(img, (imageUrl: string) => {
			const img = new Image();
			img.src = imageUrl;
			img.onload = () => {
				const { width = 0, height = 0 } = img;
				resolve({ width, height });
			};
			img.onerror = () => {
				resolve(null);
			};
		});
	});
}

export function getBase64(img: RcFile, callback: Function) {
	const reader = new FileReader();
	reader.addEventListener('load', () => callback(reader.result));
	reader.readAsDataURL(img);
}

const acceptedFileFormat = ['.jpg', '.jpeg', '.png'];
const acceptedUploadFormat = acceptedFileFormat.join(',');
const acceptedFileSize = 2;
const defaultAcceptedResolution: ImageResolution = {
	width: 4096,
	height: 4096,
};

export default function ImageFileUpload(props: FileUploadProps) {
	const formContext = useFormContext();
	const { t } = useTranslation();
	const {
		uploadLinkFunction,
		field,
		className,
		error,
		defaultImageUrl,
		defaultFileList,
		onLoadingChange,
		buttonText = t('uploader.dnd'),
		onImageChange,
		acceptedResolution = defaultAcceptedResolution,
		exactResolution,
		style,
		projectName,
		customSuccessCallback,
	} = props;
	const [loading, setLoading] = useState(false);
	const [imageUrl, setImageUrl] = useState<string | null>(
		defaultImageUrl || null,
	);
	const [progressValue, setProgressValue] = useState(0);
	const [fileList, setFileList] = useState<UploadFile[]>(defaultFileList || []);
	const showError = !loading && !!error?.message;

	useDidUpdate(() => {
		const file = fileList.filter((file) => file.status === 'done')?.[0] || null;

		const fileInfo = file
			? {
					s3Key: file.response?.s3Key,
					fileName: file.name,
					imageSrc: file.response?.imageSrc
						? file.response?.imageSrc.split('?')[0]
						: '',
				}
			: null;

		field?.onChange(fileInfo);
		onImageChange?.(fileInfo);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [fileList]);
	useEffect(() => {
		setImageUrl(defaultImageUrl || null);
	}, [defaultImageUrl]);

	useEffect(() => {
		onLoadingChange?.(loading);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [loading]);

	const handleError = (message: string) => {
		setTimeout(() => {
			formContext?.setError(field?.name || '', { message });
		}, 0);
	};

	const handleBeforeUpload = async (file: RcFile) => {
		const resolution = await getResolution(file);

		setFileList([]);
		setImageUrl(null);

		if (!isFileFormatValid(file, acceptedFileFormat)) {
			handleError(t('uploader.fileTypeError'));
			return Upload.LIST_IGNORE;
		}

		if (!isFileSizeValid(file, acceptedFileSize)) {
			handleError(
				t('uploader.sizeError.MB', {
					fileSize: acceptedFileSize,
				}),
			);
			return Upload.LIST_IGNORE;
		}

		if (!isResolutionValid(resolution, acceptedResolution)) {
			handleError(t('uploader.fileResolutionError', { ...acceptedResolution }));
			return Upload.LIST_IGNORE;
		}

		if (
			exactResolution &&
			!isExactResolutionValid(resolution, exactResolution)
		) {
			handleError(
				t('uploader.imageExactResolutionError', { ...exactResolution }),
			);
			return Upload.LIST_IGNORE;
		}

		return true;
	};

	const handleChange = (info: UploadChangeParam) => {
		setFileList(info.fileList);

		if (info.file.status === 'uploading') {
			setImageUrl(null);
			setLoading(true);
			return;
		}

		if (info.file.status === 'error') {
			setLoading(false);
			return;
		}

		if (info.file.status === 'done') {
			getBase64(info.file.originFileObj as RcFile, (imageUrl: string) => {
				setImageUrl(imageUrl);
				setLoading(false);
			});
		}
	};
	const progressIndicator = () => {
		return (
			<>
				<LoadingOutlined style={{ color: '#82DC36' }} /> <br />
				Uploading...{progressValue}%
			</>
		);
	};

	const handleRequest = async (options: UploadRequestOption) => {
		const { onSuccess, onError, file, onProgress } = options;

		setLoading(true);

		try {
			const { key, uploadLink, contentType } = await uploadLinkFunction(
				(file as RcFile).name,
			);

			const result: any = await axios.put(uploadLink, file, {
				headers: {
					'Content-Type': contentType,
				},
				onUploadProgress: (e) => {
					setProgressValue(parseFloat(((e.loaded / e.total) * 100).toFixed(2)));
					onProgress?.({ percent: (e.loaded / e.total) * 100 } as any);
				},
			});

			try {
				await api.validateImageResolution(
					key,
					acceptedResolution.height,
					acceptedResolution.width,
				);
			} catch (error: any) {
				const e = new Error(
					error?.errors?.Key?.[0] || t('uploader.uploadError'),
				);
				e.name = 'PostUploadError';
				throw e;
			}
			onSuccess?.(
				isFunction(customSuccessCallback)
					? customSuccessCallback()
					: OmniNotification.success({
							message: t('common.Success.assetUpload', {
								assetType: t('common.Image'),
							}),
							description: t('common.Success.assetUpload.description', {
								assetType: t('common.Image').toLowerCase(),
								projectName: projectName,
							}),
						}),
				result,
			);
			onSuccess?.({ ...result.body, s3Key: key }, result);
		} catch (err: any) {
			handleError(
				err?.name === 'PostUploadError'
					? err.message
					: t('uploader.uploadError'),
			);
			onError?.(err);
		} finally {
			setLoading(false);
		}
	};

	const handleRemove = (event: SyntheticEvent) => {
		event.stopPropagation();

		setFileList([]);
		setImageUrl(null);
	};

	const uploadButton = (
		<div>
			{loading ? progressIndicator() : <UploadOutlined />}
			<div>{showError ? error?.message : buttonText}</div>
		</div>
	);

	const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
		if (!isFileFormatValid(event.dataTransfer.files[0], acceptedFileFormat)) {
			handleError(t('uploader.fileTypeError'));
		}
	};

	return (
		<div
			className={cx(
				className,
				{
					[styles.containerError]: showError,
				},
				styles.uploader,
			)}
			ref={field?.ref}
			tabIndex={0}
		>
			<Upload.Dragger
				data-testid="upload-dragger"
				fileList={fileList}
				showUploadList={false}
				beforeUpload={handleBeforeUpload}
				onChange={handleChange}
				onDrop={handleDrop}
				customRequest={handleRequest}
				accept={acceptedUploadFormat}
			>
				{imageUrl ? (
					<>
						<div className={styles.uploaderImg} style={style}>
							<img src={imageUrl} alt="preview" />
							<Button
								icon={<DeleteOutlined />}
								type="link"
								onClick={handleRemove}
							/>
						</div>
					</>
				) : (
					uploadButton
				)}
			</Upload.Dragger>
		</div>
	);
}
